import {
  Typography,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
} from '@mui/material';
import usePagination from 'src/hooks/usePagination';
import CustomTable from 'src/components/table/table';
import TabTitle from 'src/components/tabTitle/tabTitle';
import { GET_BOOKING, UPDATE_BOOKING } from 'src/apiService/apiDeclaration';
import PageHeader from 'src/components/pageHeader/PageHeader';
import { bookingTableColumns, bookingDriverTableColumns } from 'src/constants/columnConfig';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { STATUS_OPTIONS_EMPLOYEE, STATUS_OPTIONS_ADMIN, removeEmptyKeys } from 'src/constants';
import MenuPopover from 'src/components/menu/menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { transformDataToCSV } from 'src/helper';
import { useModal } from 'src/hooks/useModal';
import { showErrorTost, showSuccessTost } from 'src/constants';
import { useSelector } from 'react-redux';
import { USER_ROLES } from 'src/config/enum';
import Notes from 'src/components/form/Notes';
import { ICONS } from 'src/assets/library';
import { useEffect, useState } from 'react';

const BookingPage = () => {
  const location = useLocation();
  const state = location.state;
  let params = {};
  const { user } = useSelector((state) => state.auth);
  const statusOptions = user.role === USER_ROLES.ADMIN ? STATUS_OPTIONS_ADMIN : STATUS_OPTIONS_EMPLOYEE;
  if (user.role === USER_ROLES.EMPLOYEE) {
    params.userId = user._id;
  }
  if (state?._id) {
    params.userId = state?._id;
  }
  const { setDialogData, handleModalClose } = useModal();
  const [status, setStatus] = useState([]);
  const { paginationModel, setPaginationModel, loading, data, refresh, setQuerryParams } = usePagination(
    GET_BOOKING,
    params
  );
  const navigate = useNavigate();

  const handleNavigate = (body) => {
    navigate('/booking-details', { state: body });
  };
  const onStatusUpdate = async (id, value) => {
    try {
      await UPDATE_BOOKING(id, { status: value });
      onSuccess();
    } catch (err) {
      console.log(err);
    }
  };
  const updatedBookingTable =
    user.role === USER_ROLES.ADMIN ? [...bookingTableColumns] : [...bookingDriverTableColumns(user.jobPercentage)];

  updatedBookingTable.push({
    field: 'action',
    headerName: 'Action',
    sortable: false,
    disableColumnMenu: true,
    width: 80,
    renderCell: ({ row }) => {
      return (
        <MenuPopover
          menuArray={[
            ...statusOptions.slice(1).map(({ label, value }) => ({
              name: label,
              action: () => {
                setDialogData({
                  showModal: true,
                  description: `Are you sure you want to change status of this job to ${label}?`,
                  onConfirm: () => onStatusUpdate(row._id, value),
                });
              },
            })),
            ...(user.role === USER_ROLES.ADMIN
              ? [
                  {
                    iconPath: require(`src/assets/trash.png`),
                    name: 'Move to Trash',
                    action: () => {
                      setDialogData({
                        showModal: true,
                        description: 'Are you sure you want to move this booking to trash?',
                        onConfirm: () => onDelete(row._id),
                      });
                    },
                  },
                ]
              : []),
            {
              icon: ICONS.notes,
              name: 'Add Notes',
              action: () => {
                setDialogData({
                  showModal: true,
                  child: <Notes />,
                });
              },
            },
          ]}
        />
      );
    },
  });
  async function generateCSV() {
    const data = await GET_BOOKING();
    transformDataToCSV(data);
  }
  const handleLabelClick = (value) => {
    if (status.includes(value)) {
      const updatedArray = status.filter((option) => option !== value);
      setStatus(status.filter((option) => option !== value));
      params.status = updatedArray.join(',');
      setQuerryParams(removeEmptyKeys(params));
    } else {
      const updatedArray = [...status, value];
      setStatus([...status, value]);
      params.status = updatedArray.join(',');
      setQuerryParams(removeEmptyKeys(params));
    }
  };
  const onDelete = async (id) => {
    try {
      await UPDATE_BOOKING(id, { deleted: true });
      onSuccess();
      showSuccessTost('Booking deleted successfully');
    } catch (error) {
      console.log(error);
      showErrorTost('Error in deleting booking');
    }
  };
  const onSuccess = () => {
    handleModalClose();
    refresh();
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="xl">
      <Grid container spacing={2}>
        <TabTitle title={'Bookings'} />
        <Grid item xs={12}>
          <Grid>
            <PageHeader pageTitle={'Bookings'} />
          </Grid>
          <Grid container spacing={2} mt={1} justifyContent="flex-end" alignItems={'flex-end'}>
            <Grid item xs={6} md={2}>
              <Accordion>
                <AccordionSummary
                  sx={{
                    backgroundColor: '#2065d1',
                    minHeight: '36px',
                    maxHeight: '24px',
                    borderRadius: 1,
                  }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                >
                  <Typography color={'white'} fontWeight={600}>
                    Filter {status.length > 0 && '(x' + status.length + ')'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {statusOptions.slice(1).map(({ label, value }, index) => (
                    <Grid key={index} item xs={12}>
                      <FormControlLabel
                        control={<Checkbox size="small" onClick={() => handleLabelClick(value)} />}
                        label={label}
                      />
                    </Grid>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Grid>
            {user.role === USER_ROLES.ADMIN && (
              <Grid item xs={5} md={2}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Button variant="contained" onClick={() => generateCSV()}>
                    Extract All (.xlsx)
                  </Button>
                </div>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <CustomTable
            loading={loading}
            isServerSidePagination
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            columns={updatedBookingTable}
            rows={data.rows}
            rowCount={data.rowCount}
            handleNavigate={() => handleNavigate()}
            onRowClick={handleNavigate}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default BookingPage;
